import { cn } from "@/lib/utils"

// ── Status tones ──────────────────────────────────────────────────────────────
//
// Every domain status resolves to one tone, and a tone resolves to exactly one
// semantic token. Raw Tailwind hues never appear here — see DESIGN.md, "The
// Status-Token Rule".
//
// Tones carry colour in a MARK, never in the text. Measured against `--card` in
// light mode, `text-status-available` reaches only 3.22:1, and 2.87:1 over its
// own `/10` tint — below the 4.5:1 this product promises. So a status label stays
// `text-foreground` and the dot beside it does the signalling. The label already
// names the state, which makes the dot redundant reinforcement rather than the
// sole carrier of meaning.

export type StatusTone =
  | "available"
  | "occupied"
  | "reserved"
  | "disabled"
  | "success"
  | "warning"
  | "danger"

const TONE_DOT: Record<StatusTone, string> = {
  available: "bg-status-available",
  occupied: "bg-status-occupied",
  reserved: "bg-status-reserved",
  disabled: "bg-status-disabled",
  success: "bg-success",
  warning: "bg-warning",
  danger: "bg-destructive",
}

/**
 * Tone as small coloured text. Only the three tones that clear 4.5:1 against
 * `--card` and `--background` in both themes are allowed to tint text at all;
 * everything else falls back to `foreground`. Never place these over a tinted
 * surface — the measured headroom assumes a plain card or page behind them.
 */
const TONE_TEXT: Partial<Record<StatusTone, string>> = {
  danger: "text-destructive",
  warning: "text-warning",
  success: "text-success",
}

const FALLBACK_TONE: StatusTone = "disabled"

// ── Domain mappings ───────────────────────────────────────────────────────────

const ORDER_TONE: Record<string, StatusTone> = {
  pending: "occupied",
  in_progress: "reserved",
  // `ready` and `ready_for_pickup` share a tone on purpose: operationally they
  // are the same state — plated and waiting for a handoff. The service type is
  // what distinguishes them, and the card already says it.
  ready: "available",
  ready_for_pickup: "available",
  completed: "disabled",
  picked_up: "disabled",
  cancelled: "danger",
  no_show: "warning",
}

const BILL_TONE: Record<string, StatusTone> = {
  open: "reserved",
  closed: "disabled",
  paid: "success",
  cancelled: "danger",
}

const TABLE_TONE: Record<string, StatusTone> = {
  available: "available",
  occupied: "occupied",
  reserved: "reserved",
  disabled: "disabled",
}

const PAYMENT_TONE: Record<string, StatusTone> = {
  unpaid: "occupied",
  locked: "reserved",
  paid: "success",
}

// ── Tone resolvers ────────────────────────────────────────────────────────────

export function orderTone(status: string): StatusTone {
  return ORDER_TONE[status] ?? FALLBACK_TONE
}

export function billTone(status: string): StatusTone {
  return BILL_TONE[status] ?? FALLBACK_TONE
}

export function tableTone(status: string): StatusTone {
  return TABLE_TONE[status] ?? FALLBACK_TONE
}

export function paymentTone(paymentStatus: string): StatusTone {
  return PAYMENT_TONE[paymentStatus] ?? FALLBACK_TONE
}

export type PublicMenuState = "published" | "pending" | "unpublished"

// Decision 14 (public menu, see
// docs/domains/user/redesign-2026-07/06-context-modes-and-public-menu.md): a
// slug is not a precondition for flipping the switch, so the badge needs a
// third state between "live" and "off" rather than a boolean. `published`
// must come from the server's effective state (`enabled && slug &&
// accepting_diners`, carried on `publicMenu.published`), never approximated
// from `enabled` alone -- that is the one part of this state machine the
// badge is not allowed to guess at.
const PUBLIC_MENU_TONE: Record<PublicMenuState, StatusTone> = {
  published: "available",
  pending: "occupied",
  unpublished: "disabled",
}

export function publicMenuState({ published, enabled }: { published: boolean; enabled: boolean }): PublicMenuState {
  if (published) return "published"
  if (enabled) return "pending"
  return "unpublished"
}

export function publicMenuTone(state: PublicMenuState): StatusTone {
  return PUBLIC_MENU_TONE[state]
}

// ── Class helpers ─────────────────────────────────────────────────────────────

/** A `size-2` state mark. The only place a status hue is allowed to be solid. */
export function toneDotClass(tone: StatusTone, className?: string): string {
  return cn("size-2 shrink-0 rounded-full", TONE_DOT[tone], className)
}

/**
 * Tone as text, for a short escalation phrase on a plain surface. Returns
 * `text-foreground` for tones that cannot carry text at AA.
 */
export function toneTextClass(tone: StatusTone): string {
  return TONE_TEXT[tone] ?? "text-foreground"
}

export function orderDotClass(status: string, className?: string): string {
  return toneDotClass(orderTone(status), className)
}

export function billDotClass(status: string, className?: string): string {
  return toneDotClass(billTone(status), className)
}

export function tableDotClass(status: string, className?: string): string {
  return toneDotClass(tableTone(status), className)
}

export function paymentDotClass(paymentStatus: string, className?: string): string {
  return toneDotClass(paymentTone(paymentStatus), className)
}
